import { UseQueryResult } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useLocalization } from '@/hooks/use-localization';
import { SearchFaqsResponse } from '@/queries/search';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { SearchResultsText } from './search-results-text';
import { removeEnclosingSlashes } from '@/utils/strings';
import { useContext, useMemo } from 'react';
import { AppContext } from '@/contexts/app-context';

type HumanySearchResultsProps = {
    data: UseQueryResult<SearchFaqsResponse>;
    close: () => void;
    onLinkClick?: (permalink: string) => void;
};

export const SearchFaqsResults = ({ data: { data }, close, onLinkClick }: HumanySearchResultsProps) => {
    const { localize } = useLocalization();
    const { getSetting } = useGlobalQueries();
    const humanySettings = getSetting('humany_settings');
    const { globalSearchTerm, setGlobalSearchTerm } = useContext(AppContext);

    function formatlink(permalink: string) {
        const faqPage = '/' + removeEnclosingSlashes(humanySettings?.humany_page_link || 'kundservice');
        try {
            const url = new URL(permalink);
            const path = removeEnclosingSlashes(url.pathname);
            const humanyApi = humanySettings?.humany_search_api?.toLowerCase() || '';

            const prefix = `faq/${humanyApi}-`;
            if (path.startsWith(prefix)) {
                return `${faqPage}/${path.slice(prefix.length)}`;
            }

            return faqPage;
        } catch {
            // Whatever
        }

        return faqPage;
    }

    const searchResults = useMemo(() => {
        const items = data?.hits || [];
        return items.filter(({ permalink }) => !permalink.includes('browse')).slice(0, 10);
    }, [data]);

    if (!data || globalSearchTerm === '') {
        return null;
    }

    return (
        <div data-component="humany-search-results">
            {searchResults.length === 0 ? (
                <h2 className="r-mb-4">
                    <SearchResultsText t10n="search-humany-no-results" searchValue={globalSearchTerm} />
                </h2>
            ) : (
                <>
                    <h2 className="r-mb-4">
                        <SearchResultsText t10n="search-humany-result-intro" searchValue={globalSearchTerm} />
                    </h2>
                    <div className="r-mb-4">
                        <ul className="r-list-image-[url(/assets/icons/chevron-right.svg)] r-pl-4">
                            {searchResults.map(item => (
                                <li key={item.id} className="r-pl-2">
                                    <Link
                                        onClick={e => {
                                            setGlobalSearchTerm('');

                                            if (onLinkClick) {
                                                e.preventDefault();
                                                onLinkClick?.(formatlink(item.permalink));
                                                return;
                                            }

                                            close();
                                        }}
                                        to={formatlink(item.permalink)}
                                        className="r-mb-2 r-block r-text-primary-300"
                                    >
                                        {item.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {searchResults?.length > 10 ? (
                        <Link
                            to={humanySettings?.humany_page_link || 'kundservice'}
                            className="r-font-semibold r-text-primary-300"
                            onClick={() => {
                                setGlobalSearchTerm('');
                                close();
                            }}
                        >
                            {localize('search-humany-result-show-more')}
                        </Link>
                    ) : null}
                </>
            )}
        </div>
    );
};
